/**
 * BE's `contexts` array (event/alert job → screen mapping, 2026-08-13) →
 * our nav-context ids. Routing is screen-level only (Drones, Active OI,
 * Active Surveillance, Infrastructure, RTM Services, Inventory, Apps —
 * see `CTX_LABEL` in `features/alerts/routing.ts`), so anything that
 * isn't one of our screens is dropped here rather than threaded through
 * as a sub-tab hint.
 *
 * BE is asked to send the full screen name (`surveillance`,
 * `infrastructure`, `inventory`) rather than our internal short ids —
 * `oi`/`rtm`/`drones`/`apps` are already the natural/domain-standard
 * name so those need no aliasing. This table is the one place that
 * translates BE's full names to our internal `Context` ids (kept short
 * in code — renaming the `Context` type itself would ripple through the
 * whole app for no functional gain). Kept as a plain alias table, not a
 * strict enum, so a job already sending our internal id (`surv`/`infra`/
 * `inv`) before it switches over still routes correctly.
 */
const BE_CONTEXT_ALIAS: Record<string, string> = {
  surveillance: 'surv',
  infrastructure: 'infra',
  inventory: 'inv',
};

/** Our nav-context ids — anything else in a BE `contexts` array (e.g.
 *  `c2`, a drone-detail sub-tab, not a screen) is dropped, not routed. */
const KNOWN_SCREENS = new Set(['drones', 'oi', 'surv', 'infra', 'rtm', 'inv', 'apps']);

/** Maps a raw MQTT `contexts` array to our internal screen ids.
 *  Unknown/malformed entries are dropped rather than thrown — a job
 *  sending an id we don't recognise yet shouldn't fail the whole message. */
export function mapBeContexts(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const v of raw) {
    if (typeof v !== 'string' || !v) continue;
    const mapped = BE_CONTEXT_ALIAS[v] ?? v;
    if (KNOWN_SCREENS.has(mapped) && !out.includes(mapped)) out.push(mapped);
  }
  return out;
}
